/**
 * Active Progress Subscription Policy (TV1-1979, slice 6)
 *
 * `batchProcesses/shared` is the single owner of "active batch processes + their
 * converged progress". This policy fans the per-id logic that
 * `detail/batchProgressPolicy.ts` runs for one process out over EVERY id
 * currently in `activeProcesses`: for each id it opens one
 * `BatchProcessesProgress` signal-subscription + one
 * `BatchProcessesProgressCount` query, and tears both down the moment that
 * id leaves the list.
 *
 * It also applies the uniform auto-removal criterion
 * (`shouldAutoRemoveProcess`) — auto-removing only cleanly-completed, converged,
 * error-free processes. Errored / canceled batches keep requiring manual archive.
 *
 * Memory note: pv3 signal-mode subscriptions don't auto-refetch — consumers
 * implement refetch on `{ type: 'signal' }`, debounced to coalesce bursts.
 *
 * §5 gate-less leak risk: no automated gate catches a leaked subscription; the
 * teardown-on-departure is asserted directly in the oracle test.
 */

import { Policy, PolicyContext } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { BatchProcessesSharedState } from '../state';
import { BatchProcessesSharedCommand } from '../commands';
import { shouldAutoRemoveProcess } from '../progressSelectors';

const SIGNAL_DEBOUNCE_MS = 250;

export const createActiveProgressSubscriptionPolicy = (
  backend: ClientLibraryType
): Policy<BatchProcessesSharedState, BatchProcessesSharedCommand> => {
  // id → teardown for the per-id subscription. Module-scoped to the policy
  // instance so both init() and onStateChange() share one registry.
  const managers = new Map<string, () => void>();

  const openForId = (
    processId: string,
    dispatch: PolicyContext<BatchProcessesSharedState, BatchProcessesSharedCommand>['dispatch']
  ) => {
    if ( managers.has( processId )) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let closed = false;

    const fetchCount = async () => {
      if ( closed ) return;
      try {
        const result = await backend.queries.BatchProcessesProgressCount({
          batchProcessId: processId
        });
        if ( closed ) return;
        dispatch(
          {
            type: 'batchProcesses/shared/progress/setProcessProgress',
            payload: { processId, projectedCount: Number( result?.count ) || 0 }
          },
          { source: 'api' }
        );
      } catch ( error ) {
        if ( closed ) return;
        console.error( 'Error fetching batch progress count:', error );
        dispatch(
          {
            type: 'batchProcesses/shared/progress/setProcessProgressLoading',
            payload: { processId, loading: false }
          },
          { source: 'api' }
        );
      }
    };

    const scheduleFetch = () => {
      if ( debounceTimer ) clearTimeout( debounceTimer );
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        fetchCount();
      }, SIGNAL_DEBOUNCE_MS );
    };

    dispatch(
      {
        type: 'batchProcesses/shared/progress/setProcessProgressLoading',
        payload: { processId, loading: true }
      },
      { source: 'api' }
    );
    fetchCount();

    const subscription = backend.subscriptions.BatchProcessesProgress(
      { _snapshot: false, _changes: 'signal' },
      ( change: any ) => {
        if ( change?.type === 'signal' ) scheduleFetch();
      }
    );

    managers.set( processId, () => {
      closed = true;
      if ( debounceTimer ) {
        clearTimeout( debounceTimer );
        debounceTimer = null;
      }
      if ( subscription && typeof subscription.close === 'function' ) {
        subscription.close();
      }
    });
  };

  const closeForId = (
    processId: string,
    dispatch: PolicyContext<BatchProcessesSharedState, BatchProcessesSharedCommand>['dispatch']
  ) => {
    const teardown = managers.get( processId );
    if ( !teardown ) return;
    teardown();
    managers.delete( processId );
    dispatch(
      { type: 'batchProcesses/shared/progress/removeProcessProgress', payload: { processId }},
      { source: 'api' }
    );
  };

  const reconcile = (
    ctx: PolicyContext<BatchProcessesSharedState, BatchProcessesSharedCommand>
  ) => {
    const { activeProcesses, progressByProcessId } = ctx.getState();
    const currentIds = new Set( activeProcesses.map( p => p.id ));

    // Open a subscription for every active id that doesn't have one yet.
    for ( const process of activeProcesses ) {
      openForId( process.id, ctx.dispatch );
    }

    // Tear down every subscription whose id has left the active list.
    for ( const id of [...managers.keys()]) {
      if ( !currentIds.has( id )) closeForId( id, ctx.dispatch );
    }

    // Uniform auto-removal — only clean, converged, error-free completions.
    for ( const process of activeProcesses ) {
      const projectedCount = progressByProcessId[process.id]?.projectedCount ?? null;
      if ( shouldAutoRemoveProcess( process, projectedCount )) {
        ctx.dispatch(
          { type: 'batchProcesses/shared/subscription/removeProcess', payload: { processId: process.id }},
          { source: 'api' }
        );
      }
    }
  };

  return {
    name: 'active-progress-subscription',

    init: ( ctx ) => {
      reconcile( ctx );
      return () => {
        for ( const id of [...managers.keys()]) {
          const teardown = managers.get( id );
          teardown?.();
          managers.delete( id );
        }
      };
    },

    // React whenever the active list changes (open/close subscriptions) or a
    // per-id progress entry changes (re-evaluate the convergence-based removal).
    shouldReact: ( prev, next ) =>
      prev.activeProcesses !== next.activeProcesses ||
      prev.progressByProcessId !== next.progressByProcessId,

    onStateChange: ({ ctx }) => reconcile( ctx )
  };
};
